/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { memo, useState } from "react";
import {
  EuiCallOut,
  EuiFieldText,
  EuiListGroup,
  EuiListGroupItem,
  EuiLoadingSpinner,
  EuiPopover,
} from "@inscopix/ideas-eui";
import { gql } from "@apollo/client";
import { useCellEditorToolVersionQuery } from "graphql/_Types";

gql`
  query CellEditorToolVersion($toolVersionId: Int!) {
    toolVersion: toolVersionById(id: $toolVersionId) {
      id
      compatibleVersions {
        nodes {
          id
          version
          toolSpec
          credits
          status
        }
      }
    }
  }
`;

const styles = {
  popover: css`
    & {
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
    }
  `,
  panel: css`
    max-height: max(200px, 50vh);
    overflow-y: scroll;
    align-items: center;
  `,
};

interface ToolVersionSelectorBaseProps {
  defaultVersion: { id: number; version: string };
  selectedToolVersion: { id: number; version: string };
  //   stopEditing: (cancel?: boolean) => void;
  onSelectVersion: (selectedVersion: { id: number; version: string }) => void;
}

/** Cell editor for cells in the tool version column of an analysis table */
export const ToolVersionSelectorBase = memo(function ToolVersionSelectorBase({
  selectedToolVersion,
  defaultVersion,
  onSelectVersion,
}: ToolVersionSelectorBaseProps) {
  const { data, loading } = useCellEditorToolVersionQuery({
    fetchPolicy: "cache-first",
    variables: {
      toolVersionId: defaultVersion.id,
    },
  });
  const compatibleToolVersions = data?.toolVersion?.compatibleVersions.nodes;

  const [isOpen, setIsOpen] = useState(false);

  return (
    <EuiPopover
      anchorPosition="downCenter"
      button={
        <EuiFieldText
          onClick={() => setIsOpen(true)}
          value={selectedToolVersion?.version ?? defaultVersion.version}
          onFocus={() => setIsOpen(true)}
        />
      }
      isOpen={isOpen}
      panelPaddingSize="none"
      css={styles.popover}
      closePopover={() => setIsOpen(false)}
      display="block"
      repositionOnScroll={false}
    >
      <EuiListGroup css={styles.panel} maxWidth="100%">
        {(() => {
          if (loading) {
            return <EuiLoadingSpinner />;
          } else if (compatibleToolVersions === undefined) {
            return (
              <EuiCallOut
                title="Sorry, there was an error"
                color="danger"
                iconType="error"
              >
                <p>Failed to fetch tool versions.</p>
              </EuiCallOut>
            );
          } else {
            return compatibleToolVersions.map(
              ({ id, version, toolSpec, credits, status }) => (
                <EuiListGroupItem
                  id={id.toString()}
                  key={id}
                  iconType={id === selectedToolVersion?.id ? "check" : "empty"}
                  label={version}
                  onClick={() => {
                    onSelectVersion({ id, version });
                    setIsOpen(false);
                  }}
                />
              ),
            );
          }
        })()}
      </EuiListGroup>
    </EuiPopover>
  );
});
